/**
 * Live Logs Page — Admin-only real-time log viewer.
 *
 * Features:
 *  - Hourly activity chart (last 48 h) with stacked error/warning/info bars
 *  - Click a bar to jump to that hour's logs
 *  - Auto-refresh (polling every 3 s via after_id)
 *  - Level / logger / free-text filters
 *  - Colour-coded level chips & expandable rows
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FiberManualRecord as DotIcon,
  FileDownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';
import { logsApi, LogEntry, LogHistogramBucket } from '@/services/api';

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

const LEVEL_COLOURS: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  CRITICAL: 'error',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  DEBUG: 'default',
};

const LEVEL_BG: Record<string, string> = {
  CRITICAL: 'rgba(239, 68, 68, 0.08)',
  ERROR: 'rgba(245, 158, 11, 0.08)',
  WARNING: 'rgba(234, 179, 8, 0.06)',
  INFO: 'transparent',
  DEBUG: 'rgba(148, 163, 184, 0.06)',
};

const CHART_COLOURS = {
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#6366f1',
  debug: '#94a3b8',
  critical: '#dc2626',
};

const LEVELS = ['', 'DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'] as const;

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFullDatetime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const ts = new Date(iso).getTime();
  const diffSec = Math.floor((now - ts) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const d = new Date(iso);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth()) {
    return `Yesterday ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }
  return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function shortenLogger(name: string): string {
  const parts = name.split('.');
  return parts.length > 2 ? parts.slice(-2).join('.') : name;
}

function formatChartHour(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (isToday) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${d.getDate()}/${d.getMonth() + 1} ${d.getHours().toString().padStart(2, '0')}:00`;
}

// ---------------------------------------------------------------------------
// Custom tooltip for chart
// ---------------------------------------------------------------------------
interface ChartTooltipProps {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
}

function ChartTooltipContent({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length || !label) return null;
  const d = new Date(label);
  const dateStr = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const timeStr = `${d.getHours().toString().padStart(2, '0')}:00 – ${((d.getHours() + 1) % 24).toString().padStart(2, '0')}:00`;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);

  return (
    <Paper sx={{ p: 1.5, minWidth: 160 }} elevation={4}>
      <Typography variant="caption" color="text.secondary" display="block">
        {dateStr}
      </Typography>
      <Typography variant="subtitle2" gutterBottom>
        {timeStr}
      </Typography>
      {payload
        .filter((p) => p.value > 0)
        .map((p) => (
          <Box key={p.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" sx={{ color: p.color, textTransform: 'capitalize', fontWeight: 500 }}>
              {p.name}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {p.value}
            </Typography>
          </Box>
        ))}
      <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 0.5, pt: 0.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2" fontWeight={600}>
          Total
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {total}
        </Typography>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LogsPage() {
  // Filters
  const [level, setLevel] = useState<string>('');
  const [loggerName, setLoggerName] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [afterId, setAfterId] = useState<number | undefined>(undefined);
  const [allEntries, setAllEntries] = useState<LogEntry[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Chart highlight
  const [selectedBucket, setSelectedBucket] = useState<LogHistogramBucket | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  // Tick counter to keep relative timestamps fresh (re-render every 30 s)
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // ---- Data fetching ----

  // Histogram (refreshes every 30 s)
  const { data: histogram } = useQuery({
    queryKey: ['logs-histogram'],
    queryFn: () => logsApi.getHistogram(48).then((r) => r.data),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });

  // Initial full load
  const {
    data: initialData,
    isLoading,
    refetch: refetchInitial,
  } = useQuery({
    queryKey: ['logs-initial', level, loggerName, search],
    queryFn: () =>
      logsApi
        .getLogs({
          limit: 500,
          level: level || undefined,
          logger_name: loggerName || undefined,
          search: search || undefined,
        })
        .then((r) => r.data),
    refetchOnWindowFocus: false,
  });

  // When initial data changes, reset the buffer
  useEffect(() => {
    if (initialData) {
      setAllEntries(initialData.entries);
      const maxId = initialData.entries.length > 0 ? initialData.entries[0].id : undefined;
      setAfterId(maxId);
    }
  }, [initialData]);

  // Polling for new entries
  const { data: pollData } = useQuery({
    queryKey: ['logs-poll', afterId, level, loggerName, search],
    queryFn: () =>
      logsApi
        .getLogs({
          limit: 200,
          level: level || undefined,
          logger_name: loggerName || undefined,
          search: search || undefined,
          after_id: afterId,
        })
        .then((r) => r.data),
    enabled: autoRefresh && afterId !== undefined,
    refetchInterval: 3000,
    refetchOnWindowFocus: false,
  });

  // Merge polled entries
  useEffect(() => {
    if (pollData && pollData.entries.length > 0) {
      setAllEntries((prev) => {
        const newEntries = [...pollData.entries, ...prev];
        return newEntries.slice(0, 2000);
      });
      setAfterId(pollData.entries[0].id);
    }
  }, [pollData]);

  // Debounce search input → search state (400 ms)
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ---- Handlers ----

  const handleClearFilters = useCallback(() => {
    setLevel('');
    setLoggerName('');
    setSearch('');
    setSearchInput('');
    setSelectedBucket(null);
  }, []);

  const toggleExpand = useCallback((id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Click on a chart bar → filter the log table to that hour
  const handleBarClick = useCallback(
    (data: LogHistogramBucket) => {
      if (!data || data.total === 0) return;
      setSelectedBucket(data);
      // Scroll to the table
      setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    },
    [],
  );

  // Derive displayed entries — if a bucket is selected, filter by its time range
  const displayedEntries = selectedBucket
    ? allEntries.filter((e) => {
        const ts = new Date(e.timestamp);
        const bucketStart = new Date(selectedBucket.hour);
        const bucketEnd = new Date(bucketStart.getTime() + 3600_000);
        return ts >= bucketStart && ts < bucketEnd;
      })
    : allEntries;

  const handleExport = useCallback(() => {
    const json = JSON.stringify(displayedEntries, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [displayedEntries]);

  const stats = initialData?.stats || pollData?.stats;

  // Compute error count for the stats cards
  const errorCount = (stats?.level_counts?.['ERROR'] || 0) + (stats?.level_counts?.['CRITICAL'] || 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DotIcon sx={{ fontSize: 12, color: autoRefresh ? 'success.main' : 'text.disabled' }} />
                <Typography variant="body2">Live</Typography>
              </Box>
            }
          />
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={() => refetchInitial()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download filtered logs as JSON">
            <IconButton size="small" onClick={handleExport} disabled={displayedEntries.length === 0}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats */}
      {stats && (
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2, display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="subtitle1" fontWeight={700}>{stats.total_entries}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color={errorCount > 0 ? 'error.main' : 'text.secondary'} fontWeight={errorCount > 0 ? 600 : 400}>Errors</Typography>
            <Typography variant="subtitle1" fontWeight={700} color={errorCount > 0 ? 'error.main' : 'text.primary'}>{errorCount}</Typography>
          </Box>
          {['WARNING', 'INFO', 'DEBUG'].map((lvl) => (
            <Box key={lvl} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{lvl}</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{stats.level_counts[lvl] || 0}</Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Hourly Activity Chart */}
      {histogram && histogram.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Log Activity — Last 48 Hours
            </Typography>
            {selectedBucket && (
              <Chip
                label={`Viewing: ${formatChartHour(selectedBucket.hour)} (${selectedBucket.total} entries)`}
                size="small"
                color="primary"
                onDelete={() => setSelectedBucket(null)}
              />
            )}
          </Box>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={histogram}
              margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
              onClick={(e) => {
                if (e?.activePayload?.[0]?.payload) {
                  handleBarClick(e.activePayload[0].payload as LogHistogramBucket);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
              <XAxis
                dataKey="hour"
                tickFormatter={formatChartHour}
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
                minTickGap={60}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <RechartsTooltip content={<ChartTooltipContent />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11 }}
              />
              <Bar dataKey="error" stackId="a" fill={CHART_COLOURS.error} name="Error" radius={[0, 0, 0, 0]}>
                {histogram.map((entry, idx) => (
                  <Cell
                    key={idx}
                    opacity={selectedBucket ? (entry.hour === selectedBucket.hour ? 1 : 0.3) : 1}
                  />
                ))}
              </Bar>
              <Bar dataKey="critical" stackId="a" fill={CHART_COLOURS.critical} name="Critical" />
              <Bar dataKey="warning" stackId="a" fill={CHART_COLOURS.warning} name="Warning">
                {histogram.map((entry, idx) => (
                  <Cell
                    key={idx}
                    opacity={selectedBucket ? (entry.hour === selectedBucket.hour ? 1 : 0.3) : 1}
                  />
                ))}
              </Bar>
              <Bar dataKey="info" stackId="a" fill={CHART_COLOURS.info} name="Info" radius={[2, 2, 0, 0]}>
                {histogram.map((entry, idx) => (
                  <Cell
                    key={idx}
                    opacity={selectedBucket ? (entry.hour === selectedBucket.hour ? 1 : 0.3) : 1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Search bar */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2 }} ref={tableRef}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Free-text search in messages…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
            <IconButton size="small" onClick={() => setFiltersOpen(!filtersOpen)} color={filtersOpen || level !== '' || loggerName !== '' ? 'primary' : 'default'}>
              <FilterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Filters (collapsible) */}
      <Collapse in={filtersOpen}>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Level</InputLabel>
                <Select value={level} label="Level" onChange={(e) => setLevel(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {LEVELS.filter(Boolean).map((l) => (
                    <MenuItem key={l} value={l}>
                      <Chip label={l} size="small" color={LEVEL_COLOURS[l] || 'default'} sx={{ fontWeight: 600 }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Logger prefix"
                placeholder="e.g. src.modules.bot_core"
                value={loggerName}
                onChange={(e) => setLoggerName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Tooltip title="Clear all filters">
                <IconButton onClick={handleClearFilters}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Log Table */}
      {isLoading ? (
        <Skeleton variant="rectangular" height={500} />
      ) : displayedEntries.length === 0 ? (
        <Alert severity="info">
          {selectedBucket
            ? `No log entries found for ${formatChartHour(selectedBucket.hour)}. The buffer may have rotated.`
            : 'No log entries found matching the current filters.'}
        </Alert>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(100vh - 380px)',
            overflow: 'auto',
            '& .MuiTableCell-root': { py: 1, px: 1.5, fontSize: '0.82rem' },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 30 }} />
                <TableCell sx={{ width: 100 }}>Time</TableCell>
                <TableCell sx={{ width: 80 }}>Level</TableCell>
                <TableCell sx={{ width: 220 }}>Logger</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedEntries.map((entry) => {
                const isExpanded = expandedRows.has(entry.id);
                const isLong = entry.message.length > 200 || !!entry.exception;

                return (
                  <TableRow
                    key={entry.id}
                    hover
                    sx={{
                      bgcolor: LEVEL_BG[entry.level] || 'transparent',
                      cursor: isLong ? 'pointer' : 'default',
                      '&:hover': { bgcolor: entry.level === 'ERROR' ? 'rgba(239, 68, 68, 0.12)' : undefined },
                    }}
                    onClick={() => isLong && toggleExpand(entry.id)}
                  >
                    <TableCell>
                      {isLong && (
                        <IconButton size="small" sx={{ p: 0 }}>
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatFullDatetime(entry.timestamp)}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                        >
                          {formatRelativeTime(entry.timestamp)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.level}
                        size="small"
                        color={LEVEL_COLOURS[entry.level] || 'default'}
                        sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={entry.logger}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.76rem',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {shortenLogger(entry.logger)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Collapse in={isExpanded || !isLong} collapsedSize={20}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.78rem',
                            whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                            overflow: isExpanded ? 'visible' : 'hidden',
                            textOverflow: isExpanded ? 'unset' : 'ellipsis',
                            maxWidth: isExpanded ? '100%' : 600,
                            wordBreak: isExpanded ? 'break-all' : 'normal',
                          }}
                        >
                          {entry.message}
                        </Typography>
                        {isExpanded && entry.exception && (
                          <Paper
                            variant="outlined"
                            sx={{
                              mt: 1,
                              p: 1,
                              bgcolor: 'rgba(245, 158, 11, 0.08)',
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              maxHeight: 300,
                              overflow: 'auto',
                            }}
                          >
                            {entry.exception}
                          </Paper>
                        )}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Footer */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {displayedEntries.length} entries{selectedBucket ? ` (filtered to ${formatChartHour(selectedBucket.hour)})` : ''} — most recent first
        </Typography>
        {autoRefresh && (
          <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DotIcon sx={{ fontSize: 10 }} /> Polling every 3 s
          </Typography>
        )}
      </Box>
    </Box>
  );
}
